import type { ValidationSchema, FieldError } from './types';
import { compileZodForField } from './compiler';

/**
 * Minimal questionnaire shape needed for validation.
 * Keeps this package decoupled from @helios/ui types.
 */
interface QuestionnaireInput {
  sections: {
    questions: {
      fieldKey: string;
      type: string;
    }[];
  }[];
}

type Answers = Record<string, string | string[]>;

/**
 * Validate intake answers against a validation schema.
 * Returns an array of field-level errors.
 *
 * - Only validates fields present in the questionnaire.
 * - Skips empty answers (required-ness is handled separately).
 * - Returns at most one error per field (first Zod issue).
 */
export function validateAnswers(
  questionnaire: QuestionnaireInput,
  schema: ValidationSchema,
  answers: Answers,
): FieldError[] {
  const errors: FieldError[] = [];

  for (const section of questionnaire.sections) {
    for (const question of section.questions) {
      const fieldDef = schema.fields[question.fieldKey];
      if (!fieldDef || fieldDef.rules.length === 0) continue;

      const answer = answers[question.fieldKey];
      // Skip empty — required validation is handled elsewhere
      if (answer === undefined || answer === '' || (Array.isArray(answer) && answer.length === 0)) {
        continue;
      }

      const zodSchema = compileZodForField(question.type, fieldDef.rules);
      const result = zodSchema.safeParse(answer);

      if (!result.success) {
        const firstIssue = result.error.issues[0];
        errors.push({
          fieldKey: question.fieldKey,
          message: firstIssue?.message ?? 'Invalid value',
        });
      }
    }
  }

  return errors;
}
